import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const MENU_MAX_HEIGHT = 240;
const MENU_GAP = 4;

const getMenuPosition = (triggerEl) => {
  if (!triggerEl) return null;

  const rect = triggerEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const openAbove = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;

  const width = Math.max(rect.width, 160);
  const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
  return {
    position: 'fixed',
    left,
    width,
    minWidth: width,
    maxWidth: Math.min(window.innerWidth - 16, 480),
    maxHeight: MENU_MAX_HEIGHT,
    zIndex: 'var(--z-select-menu, 20000)',
    ...(openAbove
      ? { bottom: window.innerHeight - rect.top + MENU_GAP, top: 'auto' }
      : { top: rect.bottom + MENU_GAP, bottom: 'auto' }),
  };
};

/**
 * Accessible custom select (listbox).
 * While open: Tab / Shift+Tab and Arrow keys move focus among options;
 * Enter/Space select; Escape closes and restores focus to the trigger.
 * Menu is portaled to document.body so it is not clipped by modal overflow.
 */
const AccessibleSelect = ({
  id,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  required = false,
  error = false,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const generatedId = useId();
  const listboxId = id ? `${id}-listbox` : `${generatedId}-listbox`;
  const triggerId = id || `${generatedId}-trigger`;

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPosition, setMenuPosition] = useState(null);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const optionRefs = useRef([]);

  const selectedIndex = options.findIndex(
    (option) => String(option.value) === String(value)
  );
  const selectedOption = selectedIndex >= 0 ? options[selectedIndex] : null;

  const updateMenuPosition = useCallback(() => {
    const next = getMenuPosition(triggerRef.current);
    if (next) setMenuPosition(next);
  }, []);

  const closeMenu = useCallback((restoreFocus = true) => {
    setIsOpen(false);
    setActiveIndex(-1);
    setMenuPosition(null);
    if (restoreFocus) {
      requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    const startIndex = selectedIndex >= 0 ? selectedIndex : 0;
    setMenuPosition(getMenuPosition(triggerRef.current));
    setIsOpen(true);
    setActiveIndex(options.length ? startIndex : -1);
  }, [disabled, options.length, selectedIndex]);

  const selectOption = useCallback(
    (option) => {
      onChange(option.value);
      closeMenu(true);
    },
    [closeMenu, onChange]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      const target = event.target;
      if (
        containerRef.current?.contains(target) ||
        menuRef.current?.contains(target)
      ) {
        return;
      }
      closeMenu(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, closeMenu]);

  useLayoutEffect(() => {
    if (!isOpen || !menuPosition || activeIndex < 0) return;
    optionRefs.current[activeIndex]?.focus();
  }, [isOpen, menuPosition, activeIndex]);

  const moveActive = (direction) => {
    if (!options.length) return;
    setActiveIndex((prev) => {
      const current = prev < 0 ? 0 : prev;
      if (direction === 'next') {
        return (current + 1) % options.length;
      }
      if (direction === 'prev') {
        return (current - 1 + options.length) % options.length;
      }
      if (direction === 'first') return 0;
      if (direction === 'last') return options.length - 1;
      return current;
    });
  };

  const handleTriggerKeyDown = (event) => {
    if (disabled) return;

    switch (event.key) {
      case 'Tab':
        if (isOpen && options.length) {
          event.preventDefault();
          const idx = activeIndex >= 0 ? activeIndex : 0;
          if (event.shiftKey) {
            moveActive('prev');
          } else if (document.activeElement === triggerRef.current) {
            setActiveIndex(idx);
            optionRefs.current[idx]?.focus();
          } else {
            moveActive('next');
          }
        }
        break;
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          openMenu();
        } else if (event.key === 'ArrowDown') {
          moveActive('next');
        } else if (event.key === 'ArrowUp') {
          moveActive('prev');
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          closeMenu(true);
        }
        break;
      default:
        break;
    }
  };

  const handleOptionKeyDown = (event, index) => {
    switch (event.key) {
      case 'Tab':
        event.preventDefault();
        moveActive(event.shiftKey ? 'prev' : 'next');
        break;
      case 'ArrowDown':
        event.preventDefault();
        moveActive('next');
        break;
      case 'ArrowUp':
        event.preventDefault();
        moveActive('prev');
        break;
      case 'Home':
        event.preventDefault();
        moveActive('first');
        break;
      case 'End':
        event.preventDefault();
        moveActive('last');
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        selectOption(options[index]);
        break;
      case 'Escape':
        event.preventDefault();
        closeMenu(true);
        break;
      default:
        break;
    }
  };

  // Default to form width unless the caller opts into filter/toolbar sizing.
  const resolvedClassName = className.includes('rt-select--')
    ? className
    : `rt-select--form ${className}`.trim();

  return (
    <div ref={containerRef} className={`rt-select ${resolvedClassName}`.trim()}>
      <button
        type="button"
        id={triggerId}
        ref={triggerRef}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-required={required || undefined}
        onClick={() => (isOpen ? closeMenu(true) : openMenu())}
        onKeyDown={handleTriggerKeyDown}
        className={`rt-select__trigger${error ? ' is-error' : ''}`}
      >
        <span className={`rt-select__value${selectedOption ? '' : ' is-placeholder'}`}>
          {selectedOption?.label || placeholder}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className={`rt-select__chevron${isOpen ? ' is-open' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen &&
        menuPosition &&
        createPortal(
          <ul
            id={listboxId}
            ref={menuRef}
            role="listbox"
            aria-labelledby={ariaLabelledBy || triggerId}
            style={menuPosition}
            className="rt-select__menu rt-select__menu--portal"
          >
            {options.map((option, index) => {
              const isSelected = String(value) === String(option.value);
              const isActive = index === activeIndex;
              return (
                <li
                  key={option.value === '' ? '__empty__' : String(option.value)}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={isActive ? 0 : -1}
                  ref={(el) => {
                    optionRefs.current[index] = el;
                  }}
                  onClick={() => selectOption(option)}
                  onKeyDown={(event) => handleOptionKeyDown(event, index)}
                  className={`rt-select__option${isSelected ? ' is-selected' : ''}${
                    isActive ? ' is-active' : ''
                  }`}
                >
                  {option.label}
                </li>
              );
            })}
          </ul>,
          document.body
        )}
    </div>
  );
};

export default AccessibleSelect;
